import { Instance, types } from "mobx-state-tree";
import { currentUserStoreGetter, servicesGetters } from "../utils";
import { logger } from "@fidget/common/utils";
import { IntentWithDatasetInfo } from "@closerplatform/spinner-openapi";
import { LanguageOption } from "@fidget/common/components/form/languagePicker/languagePicker";
import { flatten, template } from "lodash";
import { IAgent } from "../agent/Agent";
import { IGuest } from "../base/Guest";
import { IAttachment } from "@fidget/common/components/mstchat/chatMessage";
import { replaceText, templatePairs } from "./intentStoreUtils";
export interface IntentResponseContext {
  agent?: IAgent;
  client?: IGuest;
}

export const IntentSuggestion = types
  .model("IntentSuggestion", {
    id: types.identifier,
    intent: types.string,
    responseTemplate: types.maybe(types.string),
    active: types.boolean,
    locale: types.string
  })
  .extend((self) => {
    let context: ReadonlyArray<IAttachment>;
    const templateExecutor = template(self.responseTemplate);

    return {
      actions: {
        setContext(ctx: ReadonlyArray<IAttachment>) {
          context = ctx;
        }
      },

      views: {
        get context() {
          return context;
        },
        getCompiledResponse(responseContext: IntentResponseContext) {
          return templateExecutor(responseContext);
        },
        get prettyTemplate() {
          return replaceText(self.responseTemplate ?? "", templatePairs);
        }
      }
    };
  });
export interface IIntentSuggestion extends Instance<typeof IntentSuggestion> {}

const mapLanguageOptionToMapKey = (locale: LanguageOption): string => locale.split("-")[0]!;

export const IntentStore = types
  .model("IntentStore", {
    suggestions: types.map(types.array(IntentSuggestion)),
    suggestionsEnabled: types.maybe(types.boolean)
  })
  .views(servicesGetters)
  .views(currentUserStoreGetter)
  .views((self) => {
    const views = {
      getActiveIntentSuggestions(locale: LanguageOption): IIntentSuggestion[] {
        const key = mapLanguageOptionToMapKey(locale);
        return self.suggestions.get(key)?.filter((intent) => intent.active) ?? [];
      },

      getIntentSuggestions(locale: LanguageOption): IIntentSuggestion[] {
        const key = mapLanguageOptionToMapKey(locale);
        return self.suggestions.get(key) ?? [];
      },

      get allIntentSuggestions(): IIntentSuggestion[] {
        const x: IIntentSuggestion[][] = [...self.suggestions.values()];
        return flatten(x);
      }
    };

    return views;
  })
  .actions((self) => {
    const actions = {
      setIntentSuggestions(intents: IntentWithDatasetInfo[]) {
        if (intents.length === 0) {
          return;
        }
        const sortedIntents = intents.sort((a, b) =>
          a.intentDto.intent.localeCompare(b.intentDto.intent)
        );

        const intentSuggestions = sortedIntents.map(mapIntentDtoToIntentSuggestion);
        self.suggestions.clear();

        intentSuggestions.forEach((suggestion) => {
          if (!self.suggestions.has(suggestion.locale)) {
            self.suggestions.set(suggestion.locale, []);
          }
          self.suggestions.get(suggestion.locale)!.push(suggestion);
        });
      },

      setSuggestionsEnabled(enabled: boolean) {
        self.suggestionsEnabled = enabled;
      },

      addIntentSuggestion(newIntent: IntentWithDatasetInfo) {
        const newIntentSUggestion = mapIntentDtoToIntentSuggestion(newIntent);
        const mapKey = newIntent.intentDto.locale;
        if (!self.suggestions.has(mapKey)) {
          self.suggestions.set(mapKey, []);
        }
        self.suggestions.get(mapKey)!.unshift(newIntentSUggestion);
      },

      deleteIntentSuggestion(id: string) {
        const suggestion = flatten([...self.suggestions.values()]).find((value) => value.id === id);
        if (!suggestion || !self.suggestions.has(suggestion.locale)) {
          return;
        }

        self.suggestions.set(
          suggestion.locale,
          self.suggestions.get(suggestion.locale)!.filter((intent) => intent.id !== id)
        );
      },

      updateIntentSuggestion(updatedIntent: IntentWithDatasetInfo) {
        const locale = updatedIntent.intentDto.locale;
        if (!self.suggestions.has(locale)) {
          return;
        }

        const updatedElement = mapIntentDtoToIntentSuggestion(updatedIntent);

        const arrayWithUpdate = self.suggestions
          .get(locale)!
          .map((suggestion: IIntentSuggestion) =>
            suggestion.id === updatedElement.id ? updatedElement : suggestion
          );

        self.suggestions.set(updatedElement.locale, arrayWithUpdate);
      }
    };

    return actions;
  })
  .actions((self) => {
    const eventListenerName = "IntentStringSourceListener";

    const loadAreEnabled = async () => {
      try {
        const { enabled } = await self.spinnerService.areAISuggestionsEnabled();
        self.setSuggestionsEnabled(enabled);

        return enabled;
      } catch (e) {
        logger.error({ message: "Couldn't check if suggestions enabled", error: e });
        return;
      }
    };

    const loadSuggestionsForLocale = async (
      locale: LanguageOption
    ): Promise<IntentWithDatasetInfo[]> => {
      try {
        const allIntents = await self.spinnerService.getIntents(locale);
        const intents = allIntents.filter((intent) => !intent.intentDto.deletedAt);

        return intents;
      } catch (e) {
        logger.error({
          message: `Couldn't load intent suggestions for locale ${locale}`,
          error: e
        });

        return [];
      }
    };

    const loadSuggestions = async () => {
      const suggestionResults = await Promise.all(
        Object.values(LanguageOption).map(loadSuggestionsForLocale)
      );
      const suggestions = flatten(suggestionResults);

      self.setIntentSuggestions(suggestions);
    };

    const loadIntents = async () => {
      loadAreEnabled().then((areEnabled) => {
        if (areEnabled) {
          loadSuggestions();
        }
      });
    };

    const afterCreate = function () {
      self.eventListener.addGlobalListener({
        name: eventListenerName,
        connected() {
          loadIntents();
        }
      });
    };

    const beforeDestroy = function () {
      self.eventListener.removeGlobalListener(eventListenerName);
    };

    return {
      afterCreate,
      beforeDestroy
    };
  });

const mapIntentDtoToIntentSuggestion = ({
  intentDto
}: IntentWithDatasetInfo): IIntentSuggestion => {
  const intentSuggestion = IntentSuggestion.create({
    intent: intentDto.intent,
    responseTemplate: intentDto.response,
    id: intentDto.id,
    active: intentDto.active,
    locale: intentDto.locale
  });
  if (intentDto.context && intentDto.context instanceof Array) {
    intentSuggestion.setContext(intentDto.context);
  }

  return intentSuggestion;
};

export interface IIntentStore extends Instance<typeof IntentStore> {}
